import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearch } from '../../contexts/SearchContext';
import { useProduct } from '../../contexts/ProductContext';
import { useNavigate } from 'react-router-dom';
import SearchResult from './SearchResult';
import productService from '../../services/productService';

const SpotlightSearch = () => {
  const navigate = useNavigate();
  const { openProduct } = useProduct();
  const {
    isSpotlightOpen,
    closeSpotlight,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addToRecentSearches,
    clearRecentSearches
  } = useSearch();

  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Focus input when spotlight opens
  useEffect(() => {
    if (isSpotlightOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSpotlightOpen]);

  // Perform search with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchQuery) {
      setSearchResults([]);
      setSelectedIndex(0);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        // Search products - get all products with limit 1000
        const response = await productService.getProducts({ 
          search: searchQuery,
          limit: 1000 
        });
        const products = response.data || [];

        console.log('Fetched products:', products);

        // Filter by name or SKU
        const filtered = products.filter(product =>
          product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.sku.toLowerCase().includes(searchQuery.toLowerCase())
        );

        // Add searchQuery to results for highlighting
        const resultsWithQuery = filtered.map(p => ({ ...p, searchQuery }));

        setSearchResults(resultsWithQuery.slice(0, 8)); // Limit to 8 results
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await productService.getCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };

    if (isSpotlightOpen) {
      loadCategories();
    }
  }, [isSpotlightOpen]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isSpotlightOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            handleSelectResult(searchResults[selectedIndex]);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSpotlightOpen, searchResults, selectedIndex]);

  const handleSelectResult = (product) => {
    addToRecentSearches(product.name);
    closeSpotlight();
    openProduct(product.id);
  };

  const handleCategoryClick = (category) => {
    addToRecentSearches(category.label);
    closeSpotlight();
    navigate(`/catalog?category=${category.value}`);
  };

  const handleRecentSearchClick = (search) => {
    setSearchQuery(search);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      closeSpotlight();
    }
  };

  return (
    <AnimatePresence>
      {isSpotlightOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-start justify-center pt-20 px-4"
          onClick={handleOverlayClick}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Search Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden"
          >
            {/* Search Input */}
            <div className="border-b border-gray-200">
              <div className="flex items-center px-4 py-3">
                <svg
                  className="w-5 h-5 text-gray-400 mr-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Pesquisar produtos, SKU ou categorias..."
                  className="flex-1 text-lg outline-none placeholder-gray-400"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                <div className="ml-3 text-xs text-gray-400">
                  ESC
                </div>
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {isLoading ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="inline-flex items-center">
                    <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Pesquisando...
                  </div>
                </div>
              ) : searchQuery && searchResults.length > 0 ? (
                <div>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Produtos
                  </div>
                  {searchResults.map((product, index) => (
                    <SearchResult
                      key={product.id}
                      product={product}
                      isSelected={index === selectedIndex}
                      onSelect={handleSelectResult}
                    />
                  ))}
                </div>
              ) : searchQuery && searchResults.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  Nenhum resultado encontrado para "{searchQuery}"
                </div>
              ) : (
                <>
                  {/* Recent Searches */}
                  {recentSearches.length > 0 && (
                    <div>
                      <div className="px-4 py-2 flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Pesquisas recentes
                        </span>
                        <button
                          onClick={clearRecentSearches}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Limpar
                        </button>
                      </div>
                      {recentSearches.map((search, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(search)}
                          className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 text-left"
                        >
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-gray-700">{search}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Categories */}
                  {categories.length > 0 && (
                    <div>
                      <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Categorias
                      </div>
                      <div className="grid grid-cols-2 gap-2 p-4">
                        {categories.map((category) => (
                          <button
                            key={category.value}
                            onClick={() => handleCategoryClick(category)}
                            className="px-4 py-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            <span className="text-gray-700">{category.label}</span>
                            <span className="ml-2 text-xs text-gray-400">
                              ({category.count})
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-2 flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↑↓</kbd>
                  Navegar
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">↵</kbd>
                  Selecionar
                </span>
              </div>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">⌘K</kbd>
                Fechar
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SpotlightSearch;